import {
    type CompRef,
    type OverrideOrDefaultData,
    type Pointer,
    SYSTEM_CONTAINER_TYPE
} from '@wix/document-services-types'
import type {
    ManifestApi,
    ManifestExtensionAPI,
    BuilderComponentsExtensionAPI,
    TPAExtensionAPI,
    InnerElementsExtensionAPI,
    SlotsExtensionAPI,
    StructureExtensionAPI
} from '@wix/document-manager-extensions'
import {populateSlotWithSystemContainer} from './slotPopulationUtils'
import type {CreateExtArgs} from '@wix/document-manager-core'

const VALID_CONTAINER_TYPES = ['placeholder', 'template', 'simple']

const isValidContainerType = (containerType: string | undefined) =>
    containerType !== undefined && VALID_CONTAINER_TYPES.includes(containerType)

const getContainerData = (
    manifest: ManifestApi,
    componentRef: Pointer,
    includeInnerElements: boolean,
    filterArrayItems: boolean
) => {
    return manifest
        .getData(componentRef, includeInnerElements, [{prop: 'container'}])
        .filter(
            ({metaData, nestedDataPath}) =>
                isValidContainerType(metaData?.containerType) && (!filterArrayItems || nestedDataPath?.length === 0)
        )
}

const slotAlreadyExists = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    slotName: string,
    innerElementPath: string[] | undefined,
    existingRootSlots: string[]
): boolean => {
    const {extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    if (innerElementPath) {
        const existingInnerElementSlots = innerElements.getInnerElementSlotsName(
            componentRef as CompRef,
            innerElementPath
        )
        return existingInnerElementSlots.includes(slotName)
    }

    return existingRootSlots.includes(slotName)
}

const handleElementPopulate = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    containersList: OverrideOrDefaultData[]
) => {
    const {extensionAPI, coreConfig} = createExtArgs
    const {slots} = extensionAPI as SlotsExtensionAPI
    const {experimentInstance} = coreConfig

    const existingRootSlots = slots.getPopulatedSlotNames(componentRef as CompRef)

    const containersNeedingPopulation = containersList.filter(({path, value, prop}) => {
        if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
            // value: the id of the container in data object
            if (value) {
                return !slotAlreadyExists(createExtArgs, componentRef, value, path, existingRootSlots)
            }
        } else {
            return !slotAlreadyExists(createExtArgs, componentRef, prop, path, existingRootSlots)
        }
        return true
    })

    containersNeedingPopulation.forEach((container: OverrideOrDefaultData) => {
        const slotName = container.prop
        populateSlotWithSystemContainer(createExtArgs, componentRef, slotName, container)
    })
}

export const builderComponentsContainerPopulationHook = (createExtArgs: CreateExtArgs, componentRef: Pointer) => {
    const {extensionAPI, coreConfig} = createExtArgs
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const {tpa} = extensionAPI as TPAExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI
    const {experimentInstance} = coreConfig

    const componentType = components.getComponentType(componentRef)

    if (!builderComponents.isBuilder(componentType) || tpa.isTpaByCompType(componentType)) {
        return
    }

    if (componentType === SYSTEM_CONTAINER_TYPE) return

    const filterArrayItems = !experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')
    const containersListIncludeInnerElement = getContainerData(manifest, componentRef, true, filterArrayItems)

    handleElementPopulate(createExtArgs, componentRef, containersListIncludeInnerElement)
}
